// Reine Datums-Helfer ohne Firebase-Import - dadurch aus Jest direkt testbar.

/**
 * Firestore liefert je nach Weg einen Timestamp, ein Date oder einen ISO-String.
 * Alle drei Fälle auf ein Date bringen; ungültige Werte ergeben null statt
 * eines "Invalid Date", das erst später beim Formatieren auffaellt.
 */
export function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return isNaN(parsed.getTime()) ? null : parsed;
}

export function formatDate(value) {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export function formatDateShort(value) {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatTime(value) {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

export function formatDateTime(value) {
  const date = toDate(value);
  if (!date) return '';
  return `${formatDate(date)}, ${formatTime(date)} Uhr`;
}

/**
 * Heute, 00:00 Uhr Ortszeit.
 *
 * Die Grenze, ab der ein Termin als "kommend" gilt - `isUpcoming` rechnet in
 * ganzen Tagen, ein Termin von heute Vormittag zählt also noch dazu.
 *
 * Taugt deshalb auch als `from` für useEvents: Der Wert ist über einen Tag
 * hinweg konstant und löst dort kein Neuaufsetzen des Listeners aus - anders
 * als ein `new Date()`, das bei jedem Render eine andere Zahl ergäbe.
 */
export function startOfToday() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

/** Tage bis zum Termin. Negativ = vorbei. null wenn kein gültiges Datum. */
export function daysUntil(value) {
  const date = toDate(value);
  if (!date) return null;
  const today = startOfToday();
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
}

export function isUpcoming(value) {
  const days = daysUntil(value);
  return days !== null && days >= 0;
}

/** "In 3 Tagen" / "Heute" / "Vorbei" - für die Terminliste. */
export function relativeLabel(value) {
  const days = daysUntil(value);
  if (days === null) return '';
  if (days < 0) return 'Vorbei';
  if (days === 0) return 'Heute';
  if (days === 1) return 'Morgen';
  if (days <= 30) return `In ${days} Tagen`;
  return '';
}

const pad = (n) => String(n).padStart(2, '0');

/**
 * Für <input type="date"> - 'YYYY-MM-DD' in LOKALER Zeit.
 *
 * toISOString() wäre UTC und schöbe einen Abendtermin auf den Folgetag (bzw.
 * einen frühen Morgen auf den Vortag), weil Deutschland ein bis zwei Stunden
 * vor UTC liegt.
 */
export function toDateInputValue(value) {
  const date = toDate(value);
  if (!date) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Für <input type="time"> - 'HH:MM' in lokaler Zeit, gleiche Begründung. */
export function toTimeInputValue(value) {
  const date = toDate(value);
  if (!date) return '';
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Aus den getrennten Feldern wieder ein Date.
 *
 * Die Uhrzeit ist freiwillig; ohne sie gilt 00:00 Uhr Ortszeit. Der Tag muss
 * dabei ZUSAMMEN mit einer Zeit geparst werden: `new Date('2026-10-24')` liest
 * die Norm als UTC-Mitternacht, `new Date('2026-10-24T00:00')` dagegen als
 * lokale - derselbe Stolperstein, der in galleryAlbums.js steht.
 *
 * Gibt null zurück, wenn kein brauchbares Datum dabei herauskommt.
 */
export function fromDateTimeInputs(dateValue, timeValue) {
  if (!dateValue) return null;
  const parsed = new Date(`${dateValue}T${timeValue || '00:00'}`);
  return isNaN(parsed.getTime()) ? null : parsed;
}
